import React, { useState } from 'react';
import { Card, Descriptions, Table } from 'antd';
import { DeviceTypeSettings, ReportForDevice, RulesWithDeviceType, StbHistoryType } from '@services/types';
import Column from 'antd/lib/table/Column';
import {
  ReportForDeviceResponseStbGroupHistoryElements,
  ReportForDeviceResponseUpgradeRuleHistoryElements,
  ReportForDeviceVersionHistoryElementResponse,
} from '@openapi';
import { Link } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { getLabelByFirmwareType } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import api from '@services/api';
import RuleInfoModal from '@layouts/reports/RuleInfoModal';

interface Props {
  report: ReportForDevice;
}

const DeviceReport: React.FC<Props> = ({ report }) => {
  const { settings } = React.useContext(AppContext);
  const [ruleModal, setRuleModal] = React.useState<RulesWithDeviceType['id']>();
  const [deviceSetting, setDeviceSetting] = useState<DeviceTypeSettings>();
  const {
    device,
    deviceGroupHistoryElement,
    deviceLastConnectionElement,
    deviceUpgradeHistoryElement,
    stbGroupHistory,
    upgradeRuleHistory,
    deviceStatusVersionElement,
    deviceType,
    upgradeRule,
    versionHistory,
  } = report;

  React.useEffect(() => {
    if (report.device.deviceTypeId) {
      api.administration.getDeviceTypeSettings(report.device.deviceTypeId).then((item) => {
        setDeviceSetting(item);
      });
    }
  }, [report.device.deviceTypeId]);

  return (
    <>
      <Card title="Device details" style={{ marginBottom: '1rem' }}>
        <Descriptions labelStyle={{ fontWeight: '500', fontSize: '16px' }} layout="horizontal" column={3} bordered>
          <Descriptions.Item label="MAC address">{device.macAddress}</Descriptions.Item>
          <Descriptions.Item label="Hardware Id">{deviceType.hardwareId}</Descriptions.Item>
          <Descriptions.Item label="Group">
            <Link to={Navigation.STBGroupInfoById.replace(':id', device.groupId || '')}>{device.etc.groupName}</Link>
          </Descriptions.Item>
          <Descriptions.Item label="Serial number">{device.serialNumber}</Descriptions.Item>
          <Descriptions.Item label="Usage Id">{deviceType.usageId}</Descriptions.Item>
          <Descriptions.Item label="Upgrade rule">
            <span
              style={{ color: '#00a9f7', cursor: 'pointer', fontWeight: '500' }}
              onClick={() => {
                setRuleModal(upgradeRule.id);
              }}
            >
              {upgradeRule.name}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Device Type">{device.etc.deviceTypeName}</Descriptions.Item>
          <Descriptions.Item label="Client version">{device.clientVersion}</Descriptions.Item>
          <Descriptions.Item label="Comment">{device.comment}</Descriptions.Item>
          <Descriptions.Item label="Trace enabled">
            {device.isTraceEnabledAttributeSet ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Full reset">{device.isFullResetAttributeSet ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Light reset">{device.isLightResetAttributeSet ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="In preprod">
            {device.isMoveToPreproductionAttributeSet ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="History stored">{device.isLogHistoryAttributeSet ? 'Yes' : 'No'}</Descriptions.Item>
          {settings?.enableTags && (
            <Descriptions.Item label="Tags">
              {`${device?.isLocked ? 'Locked' : 'Unlocked'} ${
                device.pendingTags.length > 0 ? ',' : '.'
              } ${device.pendingTags.map((item) => item.command).join(', ')}`}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="New Device">{device.isNewDevice ? 'Yes' : 'No'}</Descriptions.Item>
        </Descriptions>
        <RuleInfoModal
          rule={ruleModal}
          visible={!!ruleModal}
          onClose={() => {
            setRuleModal(undefined);
          }}
        />
      </Card>

      <Card title="Device status" style={{ marginBottom: '1rem' }}>
        <Descriptions column={1}>
          <Descriptions.Item label="Creation date">{device.createdAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Connection date">{device.lastConnectionDate?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Connected since last rule change">
            {device.isSyncWithUpgradeRule ? 'Yes' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Build Profile">
            {stbGroupHistory?.elements && stbGroupHistory?.elements[0]?.bpProfile.toString().padStart(2, '0')}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions column={7} bordered={true}>
          <Descriptions.Item>Version</Descriptions.Item>
          <Descriptions.Item>LOADER 1</Descriptions.Item>
          <Descriptions.Item>FWa1</Descriptions.Item>
          <Descriptions.Item>UI/APK1</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName1}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName2}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName3}</Descriptions.Item>

          <Descriptions.Item>Target</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.loaderVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.firmwareVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.uiVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.customFirmware1Version}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.customFirmware2Version}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.target?.customFirmware3Version}</Descriptions.Item>

          <Descriptions.Item>Device</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.loaderVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.firmwareVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.uiVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.customFirmware1Version}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.customFirmware2Version}</Descriptions.Item>
          <Descriptions.Item>{deviceStatusVersionElement?.device?.customFirmware3Version}</Descriptions.Item>

          <Descriptions.Item>Try Counter</Descriptions.Item>
          <Descriptions.Item>{device?.loaderUpdateRetryCount}</Descriptions.Item>
          <Descriptions.Item>{device?.firmwareUpdateRetryCount}</Descriptions.Item>
          <Descriptions.Item>{device?.uiUpdateRetryCount}</Descriptions.Item>
          <Descriptions.Item> </Descriptions.Item>
          <Descriptions.Item> </Descriptions.Item>
          <Descriptions.Item> </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Device last group change" style={{ marginBottom: '1rem' }}>
        <Descriptions column={4} bordered={true}>
          <Descriptions.Item>Date</Descriptions.Item>
          <Descriptions.Item>Old STB Group</Descriptions.Item>
          <Descriptions.Item>New STB Group</Descriptions.Item>
          <Descriptions.Item>User</Descriptions.Item>

          <Descriptions.Item>{deviceGroupHistoryElement?.date?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item>{deviceGroupHistoryElement?.oldGroupName}</Descriptions.Item>
          <Descriptions.Item>{deviceGroupHistoryElement?.newGroupName}</Descriptions.Item>
          <Descriptions.Item>{deviceGroupHistoryElement?.userName}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Last connection" style={{ marginBottom: '1rem' }}>
        <Descriptions column={1}>
          <Descriptions.Item label="Connection date">
            {deviceLastConnectionElement?.lastConnectedAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Last connection status">
            {deviceLastConnectionElement?.lastConnectedStatus}
          </Descriptions.Item>
        </Descriptions>
        <Descriptions column={7} bordered={true}>
          <Descriptions.Item>Version</Descriptions.Item>
          <Descriptions.Item>LOADER 1</Descriptions.Item>
          <Descriptions.Item>FWa1</Descriptions.Item>
          <Descriptions.Item>UI/APK1</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName1}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName2}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName3}</Descriptions.Item>
          {deviceLastConnectionElement?.loader?.targetVersion === null &&
          deviceLastConnectionElement?.firmware?.targetVersion === null &&
          deviceLastConnectionElement?.ui?.targetVersion === null ? null : (
            <>
              <Descriptions.Item>Target</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.loader?.targetVersion}</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.firmware?.targetVersion}</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.ui?.targetVersion}</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.customFirmware1?.targetVersion}</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.customFirmware2?.targetVersion}</Descriptions.Item>
              <Descriptions.Item>{deviceLastConnectionElement?.customFirmware3?.targetVersion}</Descriptions.Item>
            </>
          )}

          <Descriptions.Item>Device</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.loader?.deviceVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.firmware?.deviceVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.ui?.deviceVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware1?.deviceVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware2?.deviceVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware3?.deviceVersion}</Descriptions.Item>

          <Descriptions.Item>Upgrade Order</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.loader?.upgradeOrderMessage}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.firmware?.upgradeOrderMessage}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.ui?.upgradeOrderMessage}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware1?.upgradeOrderMessage}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware2?.upgradeOrderMessage}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware3?.upgradeOrderMessage}</Descriptions.Item>

          <Descriptions.Item>Try Counter</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.loader?.retryCounter}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.firmware?.retryCounter}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.ui?.retryCounter}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware1?.retryCounter}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware2?.retryCounter}</Descriptions.Item>
          <Descriptions.Item>{deviceLastConnectionElement?.customFirmware3?.retryCounter}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Last upgrade" style={{ marginBottom: '1rem' }}>
        <Descriptions column={7} bordered={true}>
          <Descriptions.Item>{''}</Descriptions.Item>
          <Descriptions.Item>LOADER 1</Descriptions.Item>
          <Descriptions.Item>FWa1</Descriptions.Item>
          <Descriptions.Item>UI/APK1</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName1}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName2}</Descriptions.Item>
          <Descriptions.Item>{deviceSetting?.fwExtraName3}</Descriptions.Item>
          <Descriptions.Item>To Version</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.loader?.toVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.firmware?.toVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.ui?.toVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware1?.toVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware2?.toVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware3?.toVersion}</Descriptions.Item>
          <Descriptions.Item>From Version</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.loader?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.firmware?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.ui?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware1?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware2?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.customFirmware3?.fromVersion}</Descriptions.Item>
          <Descriptions.Item>Upgrade Date</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.loader?.upgradedAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.firmware?.upgradedAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item>{deviceUpgradeHistoryElement?.ui?.upgradedAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware1?.upgradedAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware2?.upgradedAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware3?.upgradedAt?.toLocaleString()}
          </Descriptions.Item>
          {/* TODO: First Upgrade Order is not working **/}
          <Descriptions.Item>First Upgrade Order</Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.loader?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.firmware?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.ui?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware1?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware2?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item>
            {deviceUpgradeHistoryElement?.customFirmware3?.firstUpgradeOrderAt?.toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      {device.isLogHistoryAttributeSet && (
        <Card title="Device version history" style={{ marginBottom: '1rem' }}>
          <Table
            dataSource={versionHistory || []}
            rowKey={(record: ReportForDeviceVersionHistoryElementResponse) =>
              record.changedAt?.toLocaleString() as string
            }
          >
            <Column
              title="Date"
              dataIndex="changedAt"
              render={(changedAt: ReportForDeviceVersionHistoryElementResponse['changedAt']) =>
                changedAt?.toLocaleString()
              }
            />
            <Column title="Loader" dataIndex="loaderVersion" />
            <Column title="FW" dataIndex="firmwareVersion" />
            <Column title="UI/APK" dataIndex="uiVersion" />
            <Column title="UI/APK" dataIndex="uiVersion" />
            <Column title={deviceSetting?.fwExtraName1} dataIndex="customFirmware1Version" />
            <Column title={deviceSetting?.fwExtraName2} dataIndex="customFirmware2Version" />
            <Column title={deviceSetting?.fwExtraName3} dataIndex="customFirmware3Version" />
          </Table>
        </Card>
      )}
      <Card title="STB Group history">
        <Table
          dataSource={stbGroupHistory?.elements || []}
          rowKey={(record: ReportForDeviceResponseStbGroupHistoryElements) => record.id}
        >
          <Column title="STB group" dataIndex="name" />
          <Column title="Description" dataIndex="description" />
          <Column title="Hardware Id" dataIndex="hardwareId" />
          <Column title="Usage Id" dataIndex="usageId" />
          <Column title="Upgrade rule" dataIndex="upgradeRuleName" />
          <Column
            title="Disabled"
            dataIndex="isDisabled"
            render={(isDisabled: boolean) => (isDisabled ? 'Yes' : 'No')}
          />
          <Column title="User" dataIndex="userEmail" />
          <Column
            title="Date"
            dataIndex="dateHour"
            render={(dateHour: ReportForDeviceResponseStbGroupHistoryElements['dateHour']) =>
              dateHour?.toLocaleString()
            }
          />
          <Column
            title="Quota"
            dataIndex="quota"
            render={(_text, record: StbHistoryType) =>
              record.quota ? (
                <span>
                  {record?.deployedStbGroupCount || 0} / {_text}{' '}
                  {getLabelByFirmwareType(record?.quotaFirmwareType, settings)} (+
                  {record.quotaDailyIncrement}
                  /day)
                </span>
              ) : null
            }
          />
          <Column title="Deployment Mode" dataIndex="deploymentMode" />
        </Table>
      </Card>
      <Card title="Upgrade rule history">
        <Table
          dataSource={upgradeRuleHistory?.elements || []}
          rowKey={(record: ReportForDeviceResponseUpgradeRuleHistoryElements) => record.id}
        >
          <Column title="Name" dataIndex="name" />
          <Column title="UI/APK1 Version" dataIndex="uiVersion" />
          <Column title="FWa1 Version" dataIndex="firmwareVersion" />
          <Column title="LOADER 1 Version" dataIndex="loaderVersion" />
          <Column
            title="Disabled"
            dataIndex="isDisabled"
            render={(isDisabled: boolean) => (isDisabled ? 'Yes' : 'No')}
          />
          <Column title="User" dataIndex="userEmail" />
          <Column
            title="Date"
            dataIndex="dateHour"
            render={(dateHour: ReportForDeviceResponseStbGroupHistoryElements['dateHour']) =>
              dateHour?.toLocaleString()
            }
          />
        </Table>
      </Card>
    </>
  );
};

export default React.memo(DeviceReport);
